'use strict';

const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/;

const validTime = n => Number.isSafeInteger(n) && n >= 0;

function publishedActivities(value) {
  const bad = () => {
    throw Error('INVALID_PUBLISHED_ACTIVITIES');
  };

  if (!value || typeof value.published !== 'boolean' || !Array.isArray(value.activities) || value.activities.length > 20) bad();
  if (value.serverNow !== undefined && !validTime(value.serverNow)) bad();

  if (!value.published) {
    if (value.publicationId !== null || value.activities.length !== 0) bad();
    return {
      activities: [],
      serverNow: value.serverNow
    };
  }

  if (typeof value.publicationId !== 'string' || !value.publicationId || !validTime(value.publishedAt)) bad();
  const ids = new Set(),
        activities = value.activities.map(a => {
    if (!a || typeof a.activityId !== 'string' || !ID.test(a.activityId) || ids.has(a.activityId) || a.kind !== 'limited-card-packs' || typeof a.name !== 'string' || !a.name || a.name.length > 120 || typeof a.enabled !== 'boolean' || !validTime(a.startAt) || !validTime(a.endAt) || a.endAt <= a.startAt) bad();
    ids.add(a.activityId);
    return Object.freeze({
      activityId: a.activityId,
      kind: a.kind,
      name: a.name,
      enabled: a.enabled,
      startAt: a.startAt,
      endAt: a.endAt
    });
  });
  return {
    activities,
    serverNow: value.serverNow
  };
}

function countdown(remainingMs) {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000)),
        days = Math.floor(seconds / 86400),
        hours = Math.floor(seconds % 86400 / 3600),
        minutes = Math.floor(seconds % 3600 / 60);
  return (days ? days + '天 ' : '') + [hours, minutes, seconds % 60].map(n => String(n).padStart(2, '0')).join(':');
}

function activeActivity(activities, now) {
  const activity = activities.filter(a => a.enabled && a.startAt <= now && now < a.endAt).sort((a, b) => a.endAt - b.endAt || a.activityId.localeCompare(b.activityId))[0];
  return activity ? {
    activity,
    remainingMs: activity.endAt - now,
    countdown: countdown(activity.endAt - now)
  } : null;
} // Only confirmed published timestamps can create this badge. No example dates,
// progress defaults, purchase gates or reward operations are introduced here.


function createActivityMonitor({
  read,
  onChange,
  now = Date.now,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  pollMs = 60000
}) {
  let disposed = false,
      generation = 0,
      rows = [],
      tickTimer = null,
      pollTimer = null,
      pending = null,
      serverOffset = 0;

  const clock = () => now() + serverOffset;

  function cancelTick() {
    if (tickTimer !== null) clearTimer(tickTimer);
    tickTimer = null;
  }

  function emit() {
    cancelTick();
    if (disposed) return null;
    const current = activeActivity(rows, clock());
    onChange(current);
    const starts = rows.filter(a => a.enabled && a.startAt > clock()).map(a => a.startAt - clock());
    const delay = current ? Math.min(1000, current.remainingMs) : starts.length ? Math.min(...starts) : null;
    if (delay !== null) tickTimer = setTimer(emit, Math.max(1, Math.min(delay, 2147483647)));
    return current;
  }

  async function refresh() {
    if (disposed) return null;
    if (pending) return pending;
    if (pollTimer !== null) clearTimer(pollTimer);
    pollTimer = null;
    const run = ++generation;

    pending = (async () => {
      try {
        const value = publishedActivities((await read()));
        if (disposed || run !== generation) return null;
        rows = value.activities;
        serverOffset = value.serverNow === undefined ? 0 : value.serverNow - now();
        return emit();
      } catch (error) {
        if (!disposed && run === generation) {
          rows = [];
          emit();
        }

        throw error;
      } finally {
        if (!disposed && run === generation) {
          pending = null;
          pollTimer = setTimer(() => {
            refresh().catch(() => {});
          }, pollMs);
        }
      }
    })();

    return pending;
  }

  return Object.freeze({
    refresh,
    current: () => disposed ? null : activeActivity(rows, clock()),

    dispose() {
      disposed = true;
      ++generation;
      rows = [];
      pending = null;
      cancelTick();
      if (pollTimer !== null) clearTimer(pollTimer);
      pollTimer = null;
    }

  });
}

module.exports = {
  publishedActivities,
  countdown,
  activeActivity,
  createActivityMonitor
};
